import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type AppRole = 'super_admin' | 'admin' | 'manager' | 'analyst' | 'user' | 'viewer';
export type PermissionType = 'read' | 'write' | 'delete' | 'admin' | 'export' | 'backup';

export interface UserRole {
  id: string;
  user_id: string;
  role: AppRole;
  assigned_by?: string;
  assigned_at: string;
  expires_at?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Permission {
  id: string;
  role: AppRole;
  resource: string;
  permission_type: PermissionType;
  created_at: string;
}

export interface CreateUserRoleData {
  user_id: string;
  role: AppRole;
  expires_at?: string;
}

export function useRoleManagement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch user roles
  const userRolesQuery = useQuery({
    queryKey: ['user-roles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as UserRole[];
    },
  });

  // Fetch permissions
  const permissionsQuery = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('permissions')
        .select('*')
        .order('role', { ascending: true });
      
      if (error) throw error;
      return data as Permission[];
    },
  });

  // Assign role to user
  const assignRoleMutation = useMutation({
    mutationFn: async (roleData: CreateUserRoleData) => {
      const { data, error } = await supabase
        .from('user_roles')
        .insert([{
          ...roleData,
          assigned_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles'] });
      toast({
        title: "Role assigned successfully",
        description: "The user role has been assigned.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to assign role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Remove role from user
  const removeRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles'] });
      toast({
        title: "Role removed successfully",
        description: "The user role has been removed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle role status
  const toggleRoleStatusMutation = useMutation({
    mutationFn: async ({ roleId, isActive }: { roleId: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('user_roles')
        .update({ is_active: isActive })
        .eq('id', roleId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-roles'] });
      toast({
        title: "Role status updated",
        description: "The role status has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update role status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Check if current user has specific role
  const checkUserRole = async (role: AppRole): Promise<boolean> => {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    const { data } = await supabase.rpc('has_role', {
      _user_id: user.user.id,
      _role: role
    });
    
    return data || false;
  };

  // Check if current user has specific permission
  const checkUserPermission = async (resource: string, permissionType: PermissionType): Promise<boolean> => {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return false;

    const { data } = await supabase.rpc('has_permission', {
      _user_id: user.user.id,
      _resource: resource,
      _permission_type: permissionType
    });
    
    return data || false;
  };

  return {
    userRoles: userRolesQuery.data || [],
    permissions: permissionsQuery.data || [],
    isLoading: userRolesQuery.isLoading || permissionsQuery.isLoading,
    error: userRolesQuery.error || permissionsQuery.error,
    assignRole: assignRoleMutation.mutate,
    removeRole: removeRoleMutation.mutate,
    toggleRoleStatus: toggleRoleStatusMutation.mutate,
    checkUserRole,
    checkUserPermission,
    isAssigning: assignRoleMutation.isPending,
    isRemoving: removeRoleMutation.isPending,
    isToggling: toggleRoleStatusMutation.isPending,
  };
}